import type { InvestableBucket } from "@/engine/types";
import { BUCKET_COLOR, BUCKET_ICONO } from "./buckets";

export interface FilaCascada {
  clave: string | number; nombre: string; orden: number | null; fundedPct: number | null; asignado: number | null; apartar: number | null; falta: number | null;
  anios: number | null; estrategia: string | null; bucket: InvestableBucket | null; fundedNeto?: number | null;
}

/** Cascada de objetivos: un medidor por objetivo (0-100 % financiado), en el orden en que reciben capital. No escala común: el fondo y la libertad financiera viven en órdenes de magnitud distintos. */
export function Cascada({ filas, formatoDinero, compacta = false }: { filas: FilaCascada[]; formatoDinero: (v: number | null) => string; compacta?: boolean }) {
  return (
    <ol className="flex flex-col gap-3">
      {filas.map((f) => {
        const pct = f.fundedPct === null ? 0 : Math.max(0, Math.min(1, f.fundedPct));
        const neto = f.fundedNeto === undefined || f.fundedNeto === null ? null : Math.max(0, Math.min(1, f.fundedNeto));
        return (
          <li key={f.clave} className="text-sm" tabIndex={0} aria-label={`${f.nombre}: ${Math.round(pct * 100)}% financiado, faltan ${formatoDinero(f.falta)}`}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="flex min-w-0 items-center gap-1.5 font-medium">
                {f.orden !== null && <span className="tabular w-5 shrink-0 text-xs text-tinta-suave">{f.orden}.</span>}
                {f.bucket && <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: BUCKET_COLOR[f.bucket] }} title={f.bucket} aria-hidden />}
                <span className="truncate">{f.nombre}</span>
              </span>
              <span className="tabular shrink-0 font-semibold">{f.fundedPct === null ? "—" : `${Math.round(pct * 100)}%`}</span>
            </div>
            <div className="relative mt-1.5 h-2 w-full rounded-full" style={{ background: "var(--viz-track)" }}>
              <div className="h-2 rounded-full transition-[width] duration-300 motion-reduce:transition-none" style={{ width: `${pct * 100}%`, background: "var(--viz-serie)" }} />
              {neto !== null && Math.abs(neto - pct) > 0.005 && <div className="absolute top-[-3px] h-[14px] w-0.5 rounded" style={{ left: `calc(${neto * 100}% - 1px)`, background: "var(--viz-alerta)" }} title="Con tu patrimonio neto (restando deudas)" />}
            </div>
            {!compacta && (
              <div className="mt-1 flex flex-wrap justify-between gap-x-3 text-xs text-tinta-suave">
                <span>{f.anios !== null ? `${f.anios} ${f.anios === 1 ? "año" : "años"}` : "—"}{f.estrategia ? ` · ${f.bucket ? BUCKET_ICONO[f.bucket] + " " : ""}${f.estrategia}` : ""}</span>
                <span className="tabular">{formatoDinero(f.asignado)} de {formatoDinero(f.apartar)}{f.falta !== null && f.falta > 0.5 ? ` · faltan ${formatoDinero(f.falta)}` : ""}</span>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
